/**
 * The exported CSV's "When (UTC)" column has to become local time in the browser, the same reason
 * the History tab's own table does (see EditPanelHistoryPaneComponent.formatIfTimestamp): the stored
 * instant is UTC and only the browser knows the reader's timezone. The file itself stays server-built
 * (AuditHistoryExportHelper) - this only rewrites that one column's values after download, so the
 * "one unpaged, server-generated file" guarantee the export exists for is untouched.
 *
 * Line-based rather than a full CSV re-parse: the When column is always the first field of the
 * header and of every data row (AuditHistoryExportHelper.BuildHeaders/ExportRow), and neither a
 * timestamp nor the header text can ever contain a comma or quote, so splitting each logical line on
 * its first top-level comma is enough to isolate it. Everything after that comma - including any
 * embedded commas, quotes or newlines inside Old value/New value - is passed through untouched.
 */
const WHEN_HEADER_UTC = 'When (UTC)';
const WHEN_HEADER_LOCAL = 'When';

/** Matches AuditHistoryExportHelper.ExportRow's `OccurredAt.ToString("yyyy-MM-dd HH:mm:ss")` exactly. */
const EXPORT_TIMESTAMP = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

export function convertAuditHistoryExportToLocalTime(csvText: string): string {
  if (!csvText) {
    return csvText;
  }

  // Mirrors CsvFileHelper.Parse's own BOM handling - stripped here and re-added once by the caller
  // when it builds the download Blob, so the file never ends up with zero or two.
  const content = csvText.charCodeAt(0) === 0xfeff ? csvText.slice(1) : csvText;

  const lines = splitCsvRecords(content).map((line) => {
    const commaIndex = line.indexOf(',');
    const firstField = commaIndex === -1 ? line : line.slice(0, commaIndex);
    const rest = commaIndex === -1 ? '' : line.slice(commaIndex);

    if (firstField === WHEN_HEADER_UTC) {
      return WHEN_HEADER_LOCAL + rest;
    }

    if (EXPORT_TIMESTAMP.test(firstField)) {
      return formatAsLocalTime(firstField) + rest;
    }

    // Preamble rows (Item key/Item name/Label), the blank separator line and the truncation row
    // (its When column is blank) never match either check above, and pass through as-is.
    return line;
  });

  return lines.length ? lines.join('\r\n') + '\r\n' : '';
}

/**
 * Splits on newlines that sit outside quoted fields, so a value that legitimately contains a line
 * break (a multi-line comment recorded as an Old/New value) stays one record instead of splitting in
 * two. Mirrors the quote-tracking in the backend's CsvFileHelper.Parse.
 */
function splitCsvRecords(content: string): string[] {
  const lines: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < content.length; i++) {
    const ch = content[i];

    if (ch === '"') {
      inQuotes = !inQuotes;
      current += ch;
      continue;
    }
    if (!inQuotes && ch === '\r') {
      continue;
    }
    if (!inQuotes && ch === '\n') {
      lines.push(current);
      current = '';
      continue;
    }
    current += ch;
  }

  if (current.length > 0) {
    lines.push(current);
  }

  return lines;
}

/**
 * The stored value has no offset marker, so it must be forced to parse as UTC before converting -
 * `new Date("yyyy-MM-dd HH:mm:ss")` is otherwise read as local time by the browser, which would
 * silently double-apply the offset.
 */
function formatAsLocalTime(value: string): string {
  const parsed = new Date(value.replace(' ', 'T') + 'Z');
  if (isNaN(parsed.getTime())) {
    return value;
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())} ` +
    `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}:${pad(parsed.getSeconds())}`
  );
}
